import React, { isValidElement, createElement as _createElement } from 'react';
import classnames from 'classnames';
import Flex from "../../../Flex.js";
import Theme from "../../../Theme.js";
import S from '../../../TableV2.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function Loading({
  label = 'Loading...'
}) {
  const {
    t
  } = Theme.I18N.ns('tablev2.util');
  return /*#__PURE__*/_jsx("div", {
    style: {
      height: 'calc(100% - 50px)',
      width: '100%',
      position: 'relative'
    },
    children: /*#__PURE__*/_jsx("div", {
      style: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate( -50%, -50% )'
      },
      children: t('loading', label)
    })
  });
}
export function NoData({
  label = 'No Data'
}) {
  const {
    t
  } = Theme.I18N.ns('tablev2.util');
  return /*#__PURE__*/_jsx(Flex, {
    style: {
      width: '100%',
      height: '100%'
    },
    align: "center",
    justify: "center",
    children: /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/isValidElement(label) ? label : t('noData', label)
    })
  });
}
const defaultSortByToggleProps = {
  style: {
    overflow: 'hidden'
  }
};
export function HeaderRenderer({
  tableTheadClassName,
  headerGroups,
  headerGroupClassName,
  headerClassName,
  sortAscClassName,
  sortDescClassName,
  resizerClassName,
  headerFlexVariant,
  headerRef
}) {
  const variant = headerFlexVariant || 'tableHeader';
  return /*#__PURE__*/_jsx("div", {
    ref: headerRef,
    className: tableTheadClassName,
    children: headerGroups.map(headerGroup => /*#__PURE__*/_jsx("div", {
      ...headerGroup.getHeaderGroupProps(),
      className: headerGroupClassName,
      children: headerGroup.headers.map(column => {
        return /*#__PURE__*/_jsxs("div", {
          ...column.getHeaderProps(),
          className: classnames(headerClassName, column.isSorted ? column.isSortedDesc ? sortDescClassName : sortAscClassName : ''),
          children: [/*#__PURE__*/_jsx("div", {
            className: S.sortToggle,
            ...(column?.getSortByToggleProps?.(defaultSortByToggleProps) || defaultSortByToggleProps),
            children: /*#__PURE__*/_jsxs(Flex, {
              style: {
                height: '100%'
              },
              variant: variant,
              children: [column.render('Header'), column.canFilter && column.Filter && column.render('Filter')]
            })
          }), column.canResize && /*#__PURE__*/_jsx("div", {
            ...column.getResizerProps(),
            // Fix for resizing causing sort as though header was clicked
            className: classnames(resizerClassName, column.isResizing ? 'isResizing' : '')
          })]
        });
      })
    }))
  });
}
export function cellRangeRenderer({
  cellCache,
  cellRenderer,
  columnSizeAndPositionManager,
  columnStartIndex,
  columnStopIndex,
  deferredMeasurementCache,
  horizontalOffsetAdjustment,
  isScrolling,
  isScrollingOptOut,
  parent,
  // Grid (or List or Table)
  rowSizeAndPositionManager,
  rowStartIndex,
  rowStopIndex,
  styleCache,
  verticalOffsetAdjustment,
  visibleColumnIndices,
  visibleRowIndices
}) {
  const {
    rowClassName,
    evenRowClassName,
    oddRowClassName,
    rows,
    prepareRow,
    getRowProps
  } = this.props;
  const oddCn = oddRowClassName;
  const evenCn = evenRowClassName;
  const renderedRows = [];
  let rowCells = [];

  // Browsers have native size limits for elements (eg Chrome 33M pixels, IE 1.5M pixes).
  // User cannot scroll beyond these size limitations.
  // In order to work around this, ScalingCellSizeAndPositionManager compresses offsets.
  // We should never cache styles for compressed offsets though as this can lead to bugs.
  // See issue #576 for more.
  const areOffsetsAdjusted = columnSizeAndPositionManager.areOffsetsAdjusted() || rowSizeAndPositionManager.areOffsetsAdjusted();
  const canCacheStyle = !isScrolling && !areOffsetsAdjusted;
  for (let rowIndex = rowStartIndex; rowIndex <= rowStopIndex; rowIndex++) {
    const rowDatum = rowSizeAndPositionManager.getSizeAndPositionOfCell(rowIndex);
    const row = rows[rowIndex];
    prepareRow(row);
    for (let columnIndex = columnStartIndex; columnIndex <= columnStopIndex; columnIndex++) {
      const columnDatum = columnSizeAndPositionManager.getSizeAndPositionOfCell(columnIndex);
      const isVisible = columnIndex >= visibleColumnIndices.start && columnIndex <= visibleColumnIndices.stop && rowIndex >= visibleRowIndices.start && rowIndex <= visibleRowIndices.stop;
      const key = `${rowIndex}-${columnIndex}`;
      let style;

      // Cache style objects so shallow-compare doesn't re-render unnecessarily.
      if (canCacheStyle && styleCache[key]) {
        style = styleCache[key];
      } else if (deferredMeasurementCache && !deferredMeasurementCache.has(rowIndex, columnIndex)) {
        // Position not-yet-measured cells at top/left 0,0,
        // And give them width/height of 'auto' so they can grow larger than the parent Grid if necessary.
        // Positioning them further to the right/bottom influences their measured size.
        style = {
          height: 'auto',
          left: 0,
          position: 'absolute',
          top: 0,
          width: 'auto'
        };
      } else {
        style = {
          height: rowDatum.size,
          left: columnDatum.offset + horizontalOffsetAdjustment,
          position: 'absolute',
          top: 0,
          width: columnDatum.size
        };
        styleCache[key] = style;
      }
      const cellRendererParams = {
        columnIndex,
        isScrolling,
        isVisible,
        key,
        parent,
        rowIndex,
        style
      };
      let renderedCell;

      // Avoid re-creating cells while scrolling.
      // This can lead to the same cell being created many times and can cause performance issues for "heavy" cells.
      // If a scroll is in progress- cache and reuse cells.
      // This cache will be thrown away once scrolling completes.
      // However if we are scaling scroll positions and sizes, we should also avoid caching.
      // This is because the offset changes slightly as scroll position changes and caching leads to stale values.
      // For more info refer to issue #395
      //
      // If isScrollingOptOut is specified, we always cache cells.
      // For more info refer to issue #1028
      if ((isScrollingOptOut || isScrolling) && !horizontalOffsetAdjustment && !verticalOffsetAdjustment) {
        if (!cellCache[key]) {
          cellCache[key] = cellRenderer(cellRendererParams);
        }
        renderedCell = cellCache[key];

        // If the user is no longer scrolling, don't cache cells.
        // This makes dynamic cell content difficult for users and would also lead to a heavier memory footprint.
      } else {
        renderedCell = cellRenderer(cellRendererParams);
      }
      if (renderedCell == null || renderedCell === false) {
        continue; // eslint-disable-line
      }
      if (process.env.NODE_ENV !== 'production') {
        warnAboutMissingStyle(parent, renderedCell);
      }
      rowCells.push(renderedCell);
    }
    const altCn = rowIndex % 2 === 0 ? evenCn : oddCn;
    const rowHeight = this.bodyCellMeasurerCache.rowHeight({
      index: rowIndex
    });
    const rowComp = /*#__PURE__*/_createElement("div", {
      ...row.getRowProps(getRowProps(row, {
        className: classnames(rowClassName, altCn),
        style: {
          width: '100%',
          height: rowHeight,
          position: 'absolute',
          top: rowDatum.offset + verticalOffsetAdjustment,
          zIndex: rowStopIndex - rowIndex
        }
      })),
      key: `${rowIndex}`
    }, rowCells);
    renderedRows.push(rowComp);
    rowCells = [];
  }
  return renderedRows;
}
function warnAboutMissingStyle(parent, renderedCell) {
  if (process.env.NODE_ENV !== 'production') {
    if (renderedCell) {
      // If the direct child is a CellMeasurer, then we should check its child
      // See issue #611
      if (renderedCell.type && renderedCell.type.__internalCellMeasurerFlag) {
        renderedCell = renderedCell.props.children;
      }
      if (renderedCell && renderedCell.props && renderedCell.props.style === undefined && parent.__warnedAboutMissingStyle !== true) {
        parent.__warnedAboutMissingStyle = true;
        console.warn('Rendered cell should include style property for positioning.');
      }
    }
  }
}